import React, {
    useContext,
    useRef,
    useState,
    useMemo,
    useEffect,
    useCallback,
} from 'react';
import window from 'global/window';
import { PageContext } from '../../../../contexts/ContextCreator';
import { EmitMetric } from '../../../Analytics/VisibilityPixel/VisibilityPixel';
import {
    linkify,
    getTitle,
    trimFront,
} from '../../../../utilities/ui_utilities';
import { discussionForumPostingValidTypes } from '../../../Layout/Footer/constants';

const CommunityPostContentContext = React.createContext(undefined);

const CommunityContentProvider = ({ children, content, recentContent }) => {
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [backgroundHeight, setBackgroundHeight] = useState(0);
    const [imageHeight, setImageHeight] = useState(0);
    const [deleted, setDelted] = useState(false);
    const [contentTitle, setContentTitle] = useState();
    const [contentBody, setContentBody] = useState();
    const [contentImage, setContentImage] = useState();
    const [contentDescription, setContentDescription] = useState();
    const [previewTitle, setPreviewTitle] = useState();
    const [previewDescription, setPreviewDescription] = useState();
    const [contentUrl, setContentUrl] = useState();
    const [editMode, setEditMode] = useState(false);
    const [didEdit, setDidEdit] = useState();
    const [contentDate, setContentDate] = useState();
    const [publishDate, setPublishDate] = useState();
    const [author, setAuthor] = useState(content.author);
    const [changed, setChanged] = useState(0);
    const [type, setType] = useState(content.type);
    const isCommunityDiscussionType = discussionForumPostingValidTypes.includes(
        content.type
    );
    const [streamDate, setStreamDate] = useState(content.streamDate);
    const [lastId, setLastId] = useState(content.lastId);
    const [commentCount, setCommentCount] = useState(content.commentCount);
    const [isAnon, setIsAnon] = useState(content.isAnon);
    const [comments, setComments] = useState(content.comments);
    const [url, setUrl] = useState(content.url);
    const [hash, setHash] = useState(content.hash);
    const topConvincerRef = useRef();
    const articlePictureRef = useRef();
    const { isLoggedIn } = useContext(PageContext);

    useEffect(() => {
        if (imageHeight > 0) {
            return;
        }
        let retries = 15;
        let success = false;
        const pollFunction = () => {
            retries -= 1;
            const imgChildren = articlePictureRef?.current?.querySelectorAll(
                'img'
            );
            const imgChild = imgChildren?.[0];
            const height = imgChild?.clientHeight;
            if (height > 0) {
                setImageHeight(height);
                success = true;
            }
            if (retries > 0 || !success) {
                setTimeout(pollFunction, 10);
            }
        };
        pollFunction();
    }, [imageHeight, articlePictureRef]);
    const handleOnEditCanceled = () => {
        setEditMode(false);
        setTimeout(() => {
            setChanged(changed + 1);
        }, 1200);
    };
    useEffect(() => {
        if (imageHeight === 0) {
            return;
        }

        if (isLoggedIn() && articlePictureRef?.current) {
            const backgroundOffset = windowWidth < 905 ? 80 : 120;
            const offsetTop = articlePictureRef?.current.offsetTop;
            const distanceFromTop = offsetTop + imageHeight + 50;
            setBackgroundHeight(distanceFromTop - backgroundOffset);
            return;
        }

        if (topConvincerRef?.current && articlePictureRef?.current) {
            if (windowWidth < 904) {
                const offsetHeight = articlePictureRef?.current.offsetTop;
                if (offsetHeight && imageHeight) {
                    setBackgroundHeight(offsetHeight + imageHeight - 46);
                }
                return;
            }

            const rect = topConvincerRef?.current?.getBoundingClientRect();
            const { height } = rect;
            const offsetHeight = topConvincerRef?.current.offsetTop;
            if (offsetHeight && height) {
                setBackgroundHeight(offsetHeight + height + 32);
            }
        }
    }, [
        topConvincerRef,
        windowWidth,
        articlePictureRef,
        isLoggedIn,
        imageHeight,
    ]);

    const onHandleDelete = () => {
        setContentTitle('This post has been deleted');
        setContentBody('Check out a few of our most recent posts below.');
        setDelted(true);
    };
    const handleOnEditClick = () => {
        setEditMode(true);
    };
    const windowResize = useCallback(() => {
        const _windowWidth = window?.innerWidth;
        if (_windowWidth && windowWidth !== _windowWidth) {
            setWindowWidth(_windowWidth);
        }
        const imgChildren = articlePictureRef.current?.querySelectorAll('img');
        if (imgChildren?.length) {
            const imgChild = imgChildren[0];
            const height = imgChild.clientHeight;
            setImageHeight(height);
        }
    }, [windowWidth]);

    useEffect(() => {
        windowResize();
        window.addEventListener('resize', windowResize, { passive: true });
        return () => {
            window.removeEventListener('resize', windowResize);
        };
    }, [windowResize]);

    const contentType = 'community';

    const seeMoreLink = '/community/feed';

    useMemo(() => {
        setContentDate(content.contentDate);
        setPublishDate(content.publishDate);
        setAuthor(content.author);
        setHash(content.hash);
        setType(content.type);
        setStreamDate(
            content.updatedAt ? content.updatedAt : content.streamDate
        );
        setLastId(content.lastId);
        setCommentCount(content.commetCount);
        setIsAnon(content.isAnon === 1);
        setComments(content.commets);
        setUrl(content.url);
        setDidEdit(content?.moderationOption?.status?.type === 'edit');
        if (content?.moderationOption?.status?.type === 'delete') {
            setContentTitle('This post has been deleted');
            setContentBody('Check out a few of our most recent posts below.');
            setDelted(true);
        } else {
            if (content?.type === 'community-link') {
                const parts = getTitle(content?.description);
                const trueTitle = parts.title;
                const trueDescription = linkify(parts.description);
                setContentTitle(trueTitle);
                setContentBody(trueDescription);
                setContentImage(content?.link?.previewImage);
                setPreviewTitle(content?.link?.previewTitle);
                setContentUrl(content?.link?.externalUrl);
                setPreviewDescription(content?.link?.previewDescription);
                setContentDescription(content?.previewDescription);
            }
            if (content?.type === 'community-discussion') {
                let trueDescription = content?.description
                    ? linkify(trimFront(content?.description))
                    : '';
                let trueTitle = content?.title ? linkify(content?.title) : '';
                if (trueTitle === '' || (!trueTitle && content?.title !== '')) {
                    const parts = getTitle(content?.description);
                    trueTitle = parts.title;
                    trueDescription = parts.description;
                }
                setContentTitle(trueTitle);
                setContentBody(trueDescription);
            }

            if (content?.type === 'community-photo') {
                const parts = getTitle(content?.description);
                const trueTitle = parts.title;
                const trueDescription = linkify(parts.description);

                setContentTitle(trueTitle);
                setContentBody(trueDescription);
                setContentImage(content?.image);
            }
        }
    }, [changed]);
    useEffect(() => {
        EmitMetric({
            url: `${url}`,
            community_content_location: 'single',
            impression: 1,
        });
    }, []);
    const editorRef = useRef();
    return (
        <CommunityPostContentContext.Provider
            value={{
                content,
                topConvincerRef,
                articlePictureRef,
                backgroundHeight,
                editorRef,
                contentTitle,
                contentImage,
                contentBody,
                contentUrl,
                previewTitle,
                handleOnEditCanceled,
                previewDescription,
                contentDescription,
                lastId,
                publishDate,
                deleted,
                didEdit,
                isAnon,
                author,
                contentModifiedDate: streamDate || contentDate,
                hash,
                type,
                isCommunityDiscussionType,
                windowWidth,
                windowResize,
                handleOnEditClick,
                editMode,
                isLoggedIn,
                commentCount,
                comments,
                seeMoreLink,
                onHandleDelete,
                recentContent: recentContent || [],
                contentType,
            }}
        >
            {children}
        </CommunityPostContentContext.Provider>
    );
};

const useCommunityContent = () => {
    const context = useContext(CommunityPostContentContext);
    if (context === undefined) {
        throw new Error(
            'useCommunityContent can only be used inside CommunityPostContentProvider'
        );
    }
    return context;
};

export { CommunityContentProvider, useCommunityContent };
